import { useEffect, useState } from "react";
import { api, formatApiError, fmtR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import BranchMultiSelect from "@/components/BranchMultiSelect";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Mail, Activity } from "lucide-react";

const blankLine = () => ({ name: "", qty_per_use: 1, retail_value: 0 });
const blankForm = () => ({
    name: "",
    item_name: "",
    expected_amount: "",
    type: "item",
    lines: [blankLine()],
    value_amount: 0,
    max_uses: 1,
    restrictions: "",
    expiry_days: 365,
    cost_shop_id: "none",
    allowed_branch_ids: [],
    email_template_id: "default",
    is_active: true,
});

export default function BundleTemplates() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blankForm());
    const [costShops, setCostShops] = useState([]);
    const [branches, setBranches] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [log, setLog] = useState([]);
    const [logOpen, setLogOpen] = useState(false);
    const [logDetail, setLogDetail] = useState(null);
    const [testOpen, setTestOpen] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testTplId, setTestTplId] = useState("");
    const [sending, setSending] = useState(false);

    const itnUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/payfast/itn`;

    const load = async () => setItems((await api.get("/admin/bundle-templates")).data);
    useEffect(() => {
        (async () => {
            const [tpls, cs, br, et] = await Promise.all([
                api.get("/admin/bundle-templates"),
                api.get("/admin/cost-shops"),
                api.get("/admin/branches"),
                api.get("/admin/email-templates"),
            ]);
            setItems(tpls.data);
            setCostShops(cs.data);
            setBranches(br.data);
            setEmailTemplates(et.data.templates || []);
        })();
    }, []);

    const openCreate = () => { setEditing(null); setForm(blankForm()); setOpen(true); };
    const openEdit = (t) => {
        setEditing(t);
        setForm({
            name: t.name,
            item_name: t.item_name,
            expected_amount: t.expected_amount ?? "",
            type: t.type,
            lines: t.lines?.length ? t.lines : [blankLine()],
            value_amount: t.value_amount || 0,
            max_uses: t.max_uses,
            restrictions: t.restrictions || "",
            expiry_days: t.expiry_days || 365,
            cost_shop_id: t.cost_shop_id || "none",
            allowed_branch_ids: t.allowed_branch_ids || [],
            email_template_id: t.email_template_id || "default",
            is_active: t.is_active !== false,
        });
        setOpen(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: form.name.trim(),
                item_name: form.item_name.trim(),
                expected_amount: form.expected_amount === "" ? null : parseFloat(form.expected_amount),
                type: form.type,
                lines: form.type === "item"
                    ? form.lines.map(l => ({
                        name: l.name,
                        qty_per_use: parseInt(l.qty_per_use, 10) || 1,
                        retail_value: parseFloat(l.retail_value) || 0,
                    }))
                    : [],
                value_amount: form.type === "value" ? parseFloat(form.value_amount) || 0 : 0,
                max_uses: parseInt(form.max_uses, 10) || 1,
                restrictions: form.restrictions || "",
                expiry_days: parseInt(form.expiry_days, 10) || 365,
                cost_shop_id: form.cost_shop_id === "none" ? null : form.cost_shop_id,
                allowed_branch_ids: form.allowed_branch_ids,
                email_template_id: form.email_template_id === "default" ? null : form.email_template_id,
                is_active: form.is_active,
            };
            if (editing) {
                await api.patch(`/admin/bundle-templates/${editing.id}`, payload);
                toast.success("Template updated");
            } else {
                await api.post("/admin/bundle-templates", payload);
                toast.success("Template created");
            }
            setOpen(false);
            setEditing(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onDelete = async (t) => {
        if (!window.confirm(`Delete template "${t.name}"?`)) return;
        try {
            await api.delete(`/admin/bundle-templates/${t.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const copyUrl = () => {
        navigator.clipboard.writeText(itnUrl);
        toast.success("ITN URL copied");
    };

    const openLog = async () => {
        try {
            const { data } = await api.get("/admin/payfast/itn-log?limit=50");
            setLog(data);
            setLogOpen(true);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openLogDetail = async (entry) => {
        try {
            const { data } = await api.get(`/admin/payfast/itn-log/${entry.id}`);
            setLogDetail(data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const sendTest = async () => {
        if (!testEmail) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/test-email", {
                to_email: testEmail,
                bundle_template_id: testTplId || null,
            });
            toast.success(`Sent! Resend id: ${data.resend_id?.slice(0, 12)}…`);
            setTestOpen(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    const setLine = (i, field, v) => {
        const next = [...form.lines];
        next[i] = { ...next[i], [field]: v };
        setForm({ ...form, lines: next });
    };

    return (
        <div className="p-8 max-w-6xl mx-auto" data-testid="bundle-templates-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold text-[#3E2723]">Bundle Templates</h1>
                    <p className="text-sm text-[#7A726D] mt-1">
                        Map an external PayFast product to a voucher. When the ITN webhook fires, the
                        matching template is used to issue a voucher and email it to the buyer.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={openLog} data-testid="open-itn-log-btn">
                        <Activity className="w-4 h-4 mr-2" /> ITN Log
                    </Button>
                    <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="open-test-email-btn">
                        <Mail className="w-4 h-4 mr-2" /> Send Test Email
                    </Button>
                    <Button onClick={openCreate} data-testid="new-template-btn"
                        className="bg-bc-orange hover:bg-[#D26A03] text-white">
                        <Plus className="w-4 h-4 mr-2" /> New Template
                    </Button>
                </div>
            </div>

            <Card className="mb-6 border-[#E5E0D8]">
                <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-wider text-[#7A726D]">PayFast Notify URL (ITN)</div>
                        <code className="text-sm text-[#3E2723] break-all">{itnUrl}</code>
                    </div>
                    <Button variant="outline" onClick={copyUrl} data-testid="copy-itn-url-btn">
                        <Copy className="w-4 h-4 mr-2" /> Copy
                    </Button>
                </CardContent>
            </Card>

            <Card className="border-[#E5E0D8]">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Template</TableHead>
                            <TableHead>PayFast item_name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Expected R</TableHead>
                            <TableHead>Active</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="text-center text-[#7A726D] py-8">
                                No templates yet. Create one to start receiving PayFast webhooks.
                            </TableCell></TableRow>
                        )}
                        {items.map(t => (
                            <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                                <TableCell className="font-medium">{t.name}</TableCell>
                                <TableCell><code className="text-xs">{t.item_name}</code></TableCell>
                                <TableCell className="capitalize">{t.type}</TableCell>
                                <TableCell>{t.expected_amount != null ? fmtR(t.expected_amount) : "—"}</TableCell>
                                <TableCell>{t.is_active ? "Yes" : "No"}</TableCell>
                                <TableCell className="text-right">
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)}
                                        data-testid={`edit-template-${t.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => onDelete(t)}
                                        data-testid={`delete-template-${t.id}`}>
                                        <Trash2 className="w-4 h-4 text-[#B24A45]" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* Create / Edit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Template" : "New Bundle Template"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Template name</Label>
                                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                    required data-testid="tpl-name" />
                            </div>
                            <div>
                                <Label>PayFast item_name (exact match)</Label>
                                <Input value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })}
                                    required data-testid="tpl-item-name" placeholder="e.g. 10 Cappuccino Bundle" />
                            </div>
                            <div>
                                <Label>Expected amount (R, optional)</Label>
                                <Input type="number" step="0.01" value={form.expected_amount}
                                    onChange={e => setForm({ ...form, expected_amount: e.target.value })}
                                    placeholder="Leave blank to skip check" data-testid="tpl-expected-amount" />
                            </div>
                            <div>
                                <Label>Type</Label>
                                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="tpl-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="item">Item bundle</SelectItem>
                                        <SelectItem value="value">Rand value</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {form.type === "item" ? (
                            <div className="border border-[#E5E0D8] rounded p-3 space-y-2">
                                <div className="text-sm font-medium text-[#3E2723]">Line items</div>
                                {form.lines.map((ln, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-end">
                                        <div className="col-span-6">
                                            <Label className="text-xs">Name</Label>
                                            <Input value={ln.name} onChange={e => setLine(i, "name", e.target.value)}
                                                required data-testid={`tpl-line-name-${i}`} />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="text-xs">Total qty</Label>
                                            <Input type="number" min="1" value={ln.qty_per_use}
                                                onChange={e => setLine(i, "qty_per_use", e.target.value)}
                                                data-testid={`tpl-line-qty-${i}`} />
                                        </div>
                                        <div className="col-span-3">
                                            <Label className="text-xs">Retail R</Label>
                                            <Input type="number" step="0.01" value={ln.retail_value}
                                                onChange={e => setLine(i, "retail_value", e.target.value)}
                                                data-testid={`tpl-line-value-${i}`} />
                                        </div>
                                        <Button type="button" variant="ghost" size="sm"
                                            onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}
                                            disabled={form.lines.length === 1}>
                                            <Trash2 className="w-4 h-4 text-[#B24A45]" />
                                        </Button>
                                    </div>
                                ))}
                                <Button type="button" variant="outline" size="sm"
                                    onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })}
                                    data-testid="tpl-add-line-btn">
                                    <Plus className="w-4 h-4 mr-1" /> Add line
                                </Button>
                            </div>
                        ) : (
                            <div>
                                <Label>Value amount (R)</Label>
                                <Input type="number" step="0.01" value={form.value_amount}
                                    onChange={e => setForm({ ...form, value_amount: e.target.value })}
                                    required data-testid="tpl-value-amount" />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Expiry period (days from purchase)</Label>
                                <Input type="number" min="1" value={form.expiry_days}
                                    onChange={e => setForm({ ...form, expiry_days: e.target.value })}
                                    data-testid="tpl-expiry-days" />
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {[
                                        { d: 1, l: "1 day" },
                                        { d: 7, l: "7 days" },
                                        { d: 30, l: "30 days" },
                                        { d: 90, l: "90 days" },
                                        { d: 180, l: "6 months" },
                                        { d: 365, l: "1 year" },
                                    ].map(p => (
                                        <button
                                            key={p.d}
                                            type="button"
                                            onClick={() => setForm({ ...form, expiry_days: p.d })}
                                            data-testid={`expiry-preset-${p.d}`}
                                            className={`text-xs px-2 py-1 rounded border transition-colors ${parseInt(form.expiry_days, 10) === p.d
                                                ? "bg-bc-orange text-white border-bc-orange"
                                                : "border-[#E5E0D8] text-[#7A726D] hover:border-bc-orange hover:text-bc-orange"
                                                }`}
                                        >
                                            {p.l}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-[#7A726D] mt-1">
                                    Voucher will expire {form.expiry_days || 0} day{form.expiry_days == 1 ? "" : "s"}{" "}
                                    after PayFast confirms the payment.
                                </p>
                            </div>
                            <div>
                                <Label>Cost shop</Label>
                                <Select value={form.cost_shop_id}
                                    onValueChange={v => setForm({ ...form, cost_shop_id: v })}>
                                    <SelectTrigger data-testid="tpl-cost-shop"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {costShops.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label>Restrictions</Label>
                            <Textarea value={form.restrictions} rows={2}
                                onChange={e => setForm({ ...form, restrictions: e.target.value })} />
                        </div>

                        <div>
                            <Label>Allowed branches (empty = all)</Label>
                            <BranchMultiSelect branches={branches} value={form.allowed_branch_ids}
                                onChange={v => setForm({ ...form, allowed_branch_ids: v })} />
                        </div>

                        <div>
                            <Label>Email template</Label>
                            <Select value={form.email_template_id}
                                onValueChange={v => setForm({ ...form, email_template_id: v })}>
                                <SelectTrigger data-testid="tpl-email-template"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Use default email template</SelectItem>
                                    {emailTemplates.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}{t.is_default ? " (current default)" : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-[#7A726D] mt-1">
                                Pick a specific email design for this bundle, or leave on &quot;default&quot; to use whichever template is marked default.
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <input id="tpl-active" type="checkbox" checked={form.is_active}
                                onChange={e => setForm({ ...form, is_active: e.target.checked })}
                                data-testid="tpl-active" />
                            <Label htmlFor="tpl-active">Active (will accept incoming PayFast ITNs)</Label>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-bc-orange hover:bg-[#D26A03] text-white"
                                data-testid="tpl-submit-btn">
                                {editing ? "Save changes" : "Create template"}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ITN Log dialog */}
            <Dialog open={logOpen} onOpenChange={setLogOpen}>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>PayFast ITN Log (last 50)</DialogTitle>
                    </DialogHeader>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Received</TableHead>
                                <TableHead>m_payment_id</TableHead>
                                <TableHead>item_name</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Error</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {log.length === 0 && (
                                <TableRow><TableCell colSpan={6} className="text-center text-[#7A726D] py-6">
                                    No ITN events yet.
                                </TableCell></TableRow>
                            )}
                            {log.map(e => (
                                <TableRow key={e.id} onClick={() => openLogDetail(e)}
                                    className="cursor-pointer hover:bg-[#F5EFE8]"
                                    data-testid={`itn-log-row-${e.id}`}>
                                    <TableCell className="text-xs">{e.received_at?.slice(0, 19).replace("T", " ")}</TableCell>
                                    <TableCell className="text-xs">{e.m_payment_id || "—"}</TableCell>
                                    <TableCell className="text-xs">{e.item_name || "—"}</TableCell>
                                    <TableCell className="text-xs">{e.amount_gross || "—"}</TableCell>
                                    <TableCell><code className="text-xs">{e.status}</code></TableCell>
                                    <TableCell className="text-xs text-[#B24A45]">{e.error || ""}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <p className="text-xs text-[#7A726D] mt-2">Click a row for the full raw payload & signature base string.</p>
                </DialogContent>
            </Dialog>

            {/* ITN Log DETAIL dialog */}
            <Dialog open={!!logDetail} onOpenChange={(v) => !v && setLogDetail(null)}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>ITN Event Detail</DialogTitle>
                    </DialogHeader>
                    {logDetail && (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-2 gap-3">
                                <div><div className="text-xs uppercase text-[#7A726D]">Received</div><div>{logDetail.received_at}</div></div>
                                <div><div className="text-xs uppercase text-[#7A726D]">Client IP</div><code>{logDetail.client_ip || "—"}</code></div>
                                <div><div className="text-xs uppercase text-[#7A726D]">URL Path</div><code>{logDetail.path || "—"}</code></div>
                                <div><div className="text-xs uppercase text-[#7A726D]">Content-Type</div><code>{logDetail.content_type || "—"}</code></div>
                                <div><div className="text-xs uppercase text-[#7A726D]">Status</div><code>{logDetail.status}</code></div>
                                <div><div className="text-xs uppercase text-[#7A726D]">Error</div><span className="text-[#B24A45]">{logDetail.error || "—"}</span></div>
                                <div><div className="text-xs uppercase text-[#7A726D]">User-Agent</div><code className="text-xs break-all">{logDetail.user_agent || "—"}</code></div>
                                <div><div className="text-xs uppercase text-[#7A726D]">Content-Length</div><code>{logDetail.content_length || "—"}</code></div>
                            </div>
                            <div>
                                <div className="text-xs uppercase text-[#7A726D] mb-1">Raw body preview</div>
                                <pre className="bg-[#F5EFE8] p-2 rounded text-xs whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                                    {logDetail.raw_body_preview || JSON.stringify(logDetail.raw || {}, null, 2)}
                                </pre>
                            </div>
                            <div>
                                <div className="text-xs uppercase text-[#7A726D] mb-1">Signature base string</div>
                                <pre className="bg-[#F5EFE8] p-2 rounded text-xs whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                                    {logDetail.base_string || "—"}
                                </pre>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="text-xs uppercase text-[#7A726D]">Computed signature</div>
                                    <code className="text-xs break-all">{logDetail.computed_signature || "—"}</code>
                                </div>
                                <div>
                                    <div className="text-xs uppercase text-[#7A726D]">Received signature</div>
                                    <code className="text-xs break-all">{logDetail.received_signature || "—"}</code>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Test email dialog */}
            <Dialog open={testOpen} onOpenChange={setTestOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Send Test Email</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>To email</Label>
                            <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                                placeholder="you@example.com" data-testid="test-email-to" />
                            <p className="text-xs text-[#7A726D] mt-1">
                                If your Resend domain isn&apos;t verified yet, only the account owner&apos;s email
                                will receive emails. Use <code>delivered@resend.dev</code> to test the API.
                            </p>
                        </div>
                        <div>
                            <Label>Use template (optional)</Label>
                            <Select value={testTplId || "none"} onValueChange={v => setTestTplId(v === "none" ? "" : v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Demo voucher</SelectItem>
                                    {items.map(t => (
                                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
                            <Button onClick={sendTest} disabled={sending || !testEmail}
                                className="bg-bc-orange hover:bg-[#D26A03] text-white"
                                data-testid="send-test-email-btn">
                                {sending ? "Sending…" : "Send test"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
